import {
    Button,
    Checkbox,
    Col,
    DatePicker,
    Drawer,
    Form,
    InputNumber,
    Row,
    Select,
    Spin,
    Switch,
    message,
} from 'antd'
import { debounce } from 'lodash'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { HubListItem } from '@a4b/api/src/modules/Monetisation/Puja/types'
import { Temple } from '@a4b/api/src/modules/Monetisation/Puja/types/templeTypes'
import { useAppContext } from '@/components/AppContext'
import { A4B_APPS, WEEKDAYS } from '@/utils/constants'
import {
    INTERVAL_OPTIONS,
    TEMPLE_CONFIG_DRAWER_WIDTH,
    TEMPLE_CONFIG_FORM_INITIAL_VALUES,
    TEMPLE_CONFIG_VALIDATION_MESSAGES,
    TEMPLE_DEBOUNCE_DELAY,
} from '@/modules/monetisation/puja/ManageStores/CreateStore/utils/constants'
import MultipleDatePicker from './MultipleDatePicker'
import {
    buildTempleConfigDrawerFormValues,
    buildStoreFormValuesFromTempleConfigDrawer,
    convertTempleValues,
    copyTemplesToAllDays,
    getNextCopyToAllDaysValue,
    getDatesFromConfig,
} from '@/modules/monetisation/puja/ManageStores/CreateStore/utils/helper'

const { Option } = Select

interface TempleConfigDrawerProps {
    visible: boolean
    onClose: () => void
    form: any
    hubList?: HubListItem[]
    initialStoreData?: any
    isEdit?: boolean
}

const TempleConfigDrawer: React.FC<TempleConfigDrawerProps> = ({
    visible,
    onClose,
    form,
    hubList = [],
    initialStoreData,
    isEdit = false,
}) => {
    const [drawerForm] = Form.useForm()
    const { app, networkInstance } = useAppContext()
    const [temples, setTemples] = useState<Temple[]>([])
    const [fetchingTemples, setFetchingTemples] = useState(false)
    const [previewDates, setPreviewDates] = useState<moment.Moment[]>([])
    const [copyToAllDays, setCopyToAllDays] = useState(false)
    const isFieldsDisabled =
        isEdit &&
        (initialStoreData?.puja_store_state === 'STARTED' ||
            initialStoreData?.puja_store_state === 'COMPLETED')

    const monetisationPujaApi =
        app === A4B_APPS.SRIMANDIR
            ? networkInstance.clientWithHeaders.monetisationPujaApi
            : networkInstance.clientWithHeaders.monetisationAstroPujaApi

    const fetchTemples = useCallback(
        async (title = '') => {
            setFetchingTemples(true)
            try {
                const response = await monetisationPujaApi?.templeApi?.getTemples(
                    title,
                    true,
                )
                setTemples(response.data.data.temples_list || [])
            } catch (error) {
                console.error(error)
                message.error(TEMPLE_CONFIG_VALIDATION_MESSAGES.FETCH_TEMPLES_ERROR)
            } finally {
                setFetchingTemples(false)
            }
        },
        [monetisationPujaApi],
    )

    const debouncedFetchTemples = useMemo(
        () => debounce(fetchTemples, TEMPLE_DEBOUNCE_DELAY),
        [fetchTemples],
    )

    useEffect(() => {
        if (visible) {
            fetchTemples()
        }
    }, [visible, fetchTemples])

    // Watch values for conditional rendering
    const interval = Form.useWatch('interval', drawerForm)
    const multiDay = Form.useWatch('multi_day', drawerForm)
    const multiTemple = Form.useWatch('multi_temple', drawerForm)
    const startDate = Form.useWatch('start_date', drawerForm)
    const numOfDays = Form.useWatch('num_of_days', drawerForm)
    const weekday = Form.useWatch('weekday', drawerForm)
    const customDates = Form.useWatch('custom_dates', drawerForm)

    useEffect(() => {
        if (visible && form) {
            const executionsData = form.getFieldValue('executions') || {}
            const multiDayVal = form.getFieldValue('multi_day') || false
            const multiTempleVal = form.getFieldValue('multi_temple') || false

            drawerForm.setFieldsValue(
                buildTempleConfigDrawerFormValues({
                    executionsData,
                    multiDayVal,
                    multiTempleVal,
                }),
            )
        }
    }, [visible, form, drawerForm])

    // Handle conversion between single/multi select values when mode toggles
    useEffect(() => {
        const currentValues = drawerForm.getFieldValue('date_temples')
        const newValues = convertTempleValues(currentValues, multiTemple)
        if (newValues) {
            drawerForm.setFieldsValue({ date_temples: newValues })
        }
    }, [multiTemple, drawerForm])

    // Effect to set num_of_days to 1 when multi_day is disabled
    useEffect(() => {
        if (!multiDay) {
            drawerForm.setFieldsValue({ num_of_days: 1 })
        }
    }, [multiDay, drawerForm])

    const prevMultiTempleRef = useRef<boolean | undefined>(undefined)
    const prevPreviewDatesLengthRef = useRef<number>(0)

    useEffect(() => {
        const prevMultiTempleVal = prevMultiTempleRef.current
        const prevLen = prevPreviewDatesLengthRef.current
        const currLen = previewDates.length

        setCopyToAllDays(prev =>
            getNextCopyToAllDaysValue({
                prevMultiTemple: prevMultiTempleVal,
                multiTemple,
                prevPreviewDatesLength: prevLen,
                previewDatesLength: currLen,
                copyToAllDays: prev,
            }),
        )

        prevMultiTempleRef.current = multiTemple
        prevPreviewDatesLengthRef.current = currLen
    }, [multiTemple, previewDates.length])

    // Effect to update preview dates when dependencies change
    useEffect(() => {
        const values = {
            interval,
            num_of_days: numOfDays,
            start_date: startDate,
            weekday,
            custom_dates: customDates,
        }
        setPreviewDates(getDatesFromConfig(values))
    }, [interval, startDate, numOfDays, weekday, customDates])

    // Watch first day temples and propagate changes when copyToAllDays is enabled
    const firstDayTemples = Form.useWatch(['date_temples', 0], drawerForm)
    useEffect(() => {
        if (
            copyToAllDays &&
            previewDates.length > 1 &&
            firstDayTemples !== undefined &&
            firstDayTemples !== null
        ) {
            const fields = copyTemplesToAllDays(firstDayTemples, previewDates.length)
            drawerForm.setFields(fields)
        }
    }, [firstDayTemples, copyToAllDays, drawerForm, previewDates.length])

    const handleApply = async () => {
        try {
            const values = await drawerForm.validateFields()
            const mainHubId = form.getFieldValue('hub_id')

            const update = buildStoreFormValuesFromTempleConfigDrawer({
                values,
                getDatesFromConfig,
                mainHubId,
            })

            form.setFieldsValue(update)
            message.success(`Configured ${update.executions.executions.length} executions`)
            onClose()
        } catch (e) {
            console.error('Validation failed:', e)
        }
    }

    return (
        <Drawer
            title='MTMD Config'
            width={TEMPLE_CONFIG_DRAWER_WIDTH}
            onClose={onClose}
            visible={visible}
            bodyStyle={{ paddingBottom: 80 }}
            footer={
                <div style={{ textAlign: 'right' }}>
                    <Button onClick={onClose} style={{ marginRight: 8 }}>
                        {isFieldsDisabled ? 'Close' : 'Cancel'}
                    </Button>
                    {!isFieldsDisabled && (
                        <Button onClick={handleApply} type='primary'>
                            Apply
                        </Button>
                    )}
                </div>
            }
        >
            <Form
                form={drawerForm}
                layout='vertical'
                disabled={isFieldsDisabled}
                initialValues={TEMPLE_CONFIG_FORM_INITIAL_VALUES}
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name='multi_day'
                            label='Multi Day'
                            valuePropName='checked'
                        >
                            <Switch />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name='multi_temple'
                            label='Multi Temple'
                            valuePropName='checked'
                        >
                            <Switch />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name='start_date'
                            label='Start Date'
                            rules={[
                                {
                                    required: true,
                                    message: TEMPLE_CONFIG_VALIDATION_MESSAGES.START_DATE_REQUIRED,
                                },
                            ]}
                        >
                            <DatePicker
                                style={{ width: '100%' }}
                                showTime
                                format='YYYY-MM-DD HH:mm:ss'
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name='interval'
                            label='Interval'
                            rules={[
                                {
                                    required: true,
                                    message: TEMPLE_CONFIG_VALIDATION_MESSAGES.INTERVAL_REQUIRED,
                                },
                            ]}
                        >
                            <Select placeholder='Select interval'>
                                {INTERVAL_OPTIONS.map(option => (
                                    <Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name='num_of_days'
                            label='Number of Days/Executions'
                            rules={[
                                {
                                    required: true,
                                    message:
                                        TEMPLE_CONFIG_VALIDATION_MESSAGES.NUM_OF_DAYS_REQUIRED,
                                },
                            ]}
                        >
                            <InputNumber min={1} style={{ width: '100%' }} disabled={!multiDay} />
                        </Form.Item>
                    </Col>

                    {interval === 'weekly' && (
                        <Col span={12}>
                            <Form.Item
                                name='weekday'
                                label='Weekday'
                                rules={[
                                    {
                                        required: true,
                                        message:
                                            TEMPLE_CONFIG_VALIDATION_MESSAGES.WEEKDAY_REQUIRED,
                                    },
                                ]}
                            >
                                <Select placeholder='Select weekday'>
                                    {WEEKDAYS.map(day => (
                                        <Option key={day.value} value={day.value}>
                                            {day.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    )}
                </Row>

                {interval === 'custom' && (
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name='custom_dates'
                                label='Custom Dates'
                                rules={[
                                    {
                                        required: true,
                                        message:
                                            TEMPLE_CONFIG_VALIDATION_MESSAGES.CUSTOM_DATES_REQUIRED,
                                    },
                                ]}
                            >
                                {/* Implementing a simple multiple date picker list since standard one doesn't exist */}
                                <MultipleDatePicker maxDates={numOfDays} />
                            </Form.Item>
                        </Col>
                    </Row>
                )}

                {(multiTemple || multiDay) && (
                    <>
                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <h4 style={{ marginBottom: 16 }}>
                                    Select Temples for Each Date
                                </h4>
                            </Col>
                            {previewDates.map((date, index) => (
                                <Col span={24} key={index}>
                                    <Row align='middle' gutter={16}>
                                        <Col span={6}>
                                            <span>{date.format('DD MMM YYYY (ddd)')}</span>
                                        </Col>
                                        <Col
                                            span={index === 0 && previewDates.length > 1 ? 14 : 18}
                                        >
                                            {index === 0 && previewDates.length > 1 && (
                                                <div style={{ marginBottom: 4 }}>
                                                    <Checkbox
                                                        checked={copyToAllDays}
                                                        onChange={e => setCopyToAllDays(e.target.checked)}
                                                        disabled={isFieldsDisabled || !multiTemple}
                                                    >
                                                        Copy to all days
                                                    </Checkbox>
                                                </div>
                                            )}
                                        </Col>
                                        <Col span={18}>
                                            <Form.Item
                                                name={['date_temples', index]}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message:
                                                            TEMPLE_CONFIG_VALIDATION_MESSAGES.TEMPLE_REQUIRED,
                                                    },
                                                ]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Select
                                                    mode={multiTemple ? 'multiple' : undefined}
                                                    placeholder='Select temples'
                                                    filterOption={false}
                                                    showSearch
                                                    onSearch={debouncedFetchTemples}
                                                    notFoundContent={
                                                        fetchingTemples ? <Spin size='small' /> : null
                                                    }
                                                    loading={fetchingTemples}
                                                    disabled={index !== 0 && copyToAllDays}
                                                    style={{ width: '100%' }}
                                                >
                                                    {temples.map(temple => (
                                                        <Option key={temple.id} value={temple.id}>
                                                            {temple.title_en || temple.title_hi}
                                                        </Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Col>
                            ))}
                        </Row>
                        {/* Fallback hidden field to keep Form happy if we needed backward compat, or just remove old field */}
                    </>
                )}
            </Form>
        </Drawer>
    )
}

export default TempleConfigDrawer
